import React, {useMemo, useState} from 'react';

import {type OverlayProps, PopoverForm} from 'app/general/overlays';
import {useTeams} from 'app/teams/api';
import {convertLocationParamsToFetchParams} from 'app/teams/utils';
import {gt} from 'app/utils/i18n';

import {GenericPopover} from './GenericPopover';

import type {TeamSerializedType} from 'sweet-types';

export interface GenericAttachedItemProps {
  item: TeamSerializedType;
}

interface Props<P extends object> {
  overlayProps: OverlayProps;
  attached?: Array<TeamSerializedType>;
  itemURL: string;
  addItemToTeam: (team: TeamSerializedType, url: string) => Promise<unknown>;
  removeItemsFromTeam: (team: TeamSerializedType) => Promise<unknown>;
  AttachedItemComponent: React.ComponentType<
    GenericAttachedItemProps & P & InjectedProps
  >;
  attachedItemComponentProps: P;
  onSuccess: () => Promise<unknown>;
}

export function AddToTeamPopover<P extends object>({
  overlayProps,
  itemURL,
  attached,
  addItemToTeam,
  onSuccess,
  removeItemsFromTeam,
  AttachedItemComponent,
  attachedItemComponentProps,
}: Props<P>) {
  const [search, setSearch] = useState<string>('');

  const {data: teams} = useTeams(
    convertLocationParamsToFetchParams({
      search,
    }),
    {refetchOnMountOrArgChange: false}
  );

  const unAttachedTeams = useMemo(() => {
    return teams?.results.filter(
      (team) => !attached?.some((attachedTeam) => team.id === attachedTeam.id)
    );
  }, [teams, attached]);

  function doAddItemToTeam(team) {
    return addItemToTeam(team, itemURL).then(onSuccess);
  }

  function onRemove(team) {
    return removeItemsFromTeam(team).then(onSuccess);
  }
  return (
    <PopoverForm overlayProps={overlayProps}>
      {({close}) => {
        return (
          <GenericPopover<TeamSerializedType, P & InjectedProps>
            topHeading={gt('Teams')}
            bottomHeading={gt('Add to team')}
            noResultsHeading={gt('No teams found')}
            noResultsIcon="fa-users"
            loadingText={gt('Loading teams')}
            addTooltipText={gt('Add to this team')}
            attachedItems={attached}
            AttachedItemComponent={AttachedItemComponent}
            attachedItemComponentProps={{
              onRemove,
              ...attachedItemComponentProps,
            }}
            unAttachedItems={unAttachedTeams}
            search={search}
            onSearch={setSearch}
            onAdd={doAddItemToTeam}
            onClose={close}
          />
        );
      }}
    </PopoverForm>
  );
}

interface InjectedProps {
  onRemove: (item: TeamSerializedType) => Promise<unknown>;
}
